'use es6';

import { getElementBounds } from '../dom/bounds';

/**
 * Helper to get the appropriate getBounds function for a specific node position config
 * @param  {Object} subConfig a specific node position config object
 * @return {Function} either the getBounds function defined for this config object, or the default one
 */
export const getBoundsFuncForConfig = config => config.getElementBounds || getElementBounds;

/**
 * Helper to get the appropriate getElements function for a specific node position config
 * @param  {Object} subConfig a specific node position config object
 * @return {Function}  the getElements function defined for this config object
 */
export const getElementsFuncForConfig = config => config.getElements;

/**
 * Default function to get the the id used when arranging positions in an object by id
 * @param  {Object} nodePosition
 * @return {String} a string id
 */
const getBasicPositionLookupId = nodePosition => nodePosition.id;

/**
 * Helper to get the the id used when arranging positions in an object by id (per config)
 * @param  {Object} subConfig a specific node position config object
 * @return {Function}  the getPositionLookupId function defined for this config object
 */
export const getPositionLookupIdFuncForConfig = subConfig => subConfig.getPositionLookupId || getBasicPositionLookupId;

/**
 * Arrange node position subset into an object keyed by id (or uniquePositionId if available)
 * @param  {Array} nodePositions [{id: 123}, {id: 456}]
 * @return {Object} node position subset by id {123: {id: 123}, 456: {id: 456}}
 */
export const arrangeNodePositionSubsetById = (nodePositionSubset, subConfig) => {
  const nodePositionSubsetById = {};
  if (Array.isArray(nodePositionSubset)) {
    nodePositionSubset.forEach(positionInfo => {
      const idToUse = getPositionLookupIdFuncForConfig(subConfig)(positionInfo);
      nodePositionSubsetById[idToUse] = positionInfo;
    });
  } else if (nodePositionSubset) {
    Object.keys(nodePositionSubset).forEach(key => {
      nodePositionSubsetById[key] = arrangeNodePositionSubsetById(nodePositionSubset[key], subConfig);
    });
  }
  return nodePositionSubsetById;
};

/**
 * Arrange node positions into an object keyed by id
 * @param {Object} nodePositions {topLevelKey: [{id: 123}, {id: 456}]}
 * @return {Object} node positions by id {topLevelKey: {123: {id: 123}, 456: {id: 456}}}
 */
export const arrangeNodePositionsById = (nodePositions = {}, config) => {
  const nodePositionsById = {};
  Object.keys(nodePositions).forEach(key => {
    nodePositionsById[key] = arrangeNodePositionSubsetById(nodePositions[key], config[key]);
  });
  return nodePositionsById;
};